<template>
<v-hover v-slot:default="{ hover }">
  <v-card color="blue5" :elevation="hover ? 8 : 2" @click="doNavigation">
    <v-overlay absolute :value="isRestricted" z-index="0" opacity=".25">
      <v-icon size="4em" color="black" style="opacity: 0.5">mdi-lock</v-icon>
    </v-overlay>
    <v-card-title class="pb-1">
      <v-row>
        <v-col v-if="client.businessName" cols="12" class="text-left py-0 subtitle-2">{{ client.businessName }}</v-col>
        <v-col cols="12" class="text-left py-0 subtitle-2">{{ displayName }}</v-col>
        <v-col cols="12" class="text-left py-0 caption word-wrap">{{ custTypesDisplayText }}</v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row v-if="client.address && client.address !== ''">
        <v-col cols="auto" class="py-0 text-left no-wrap">
          <v-icon left>mdi-home</v-icon>
          {{ client.address }}
        </v-col>
      </v-row>
      <v-row v-if="client.primaryPhone && client.primaryPhone !== ''">
        <v-col cols="12" class="py-0 text-left">
          <v-icon left>mdi-phone</v-icon>
          {{ client.primaryPhone }}
        </v-col>
      </v-row>
      <v-row v-if="client.primaryEmail && client.primaryEmail !== ''">
        <v-col cols="12" class="py-0 text-left no-wrap">
          <v-icon left>mdi-email</v-icon>
          {{ client.primaryEmail }}
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6" class="py-0 pt-2 text-left">
          <span v-if="client.producer && client.producer !=- ''" class=".subtitle-1 font-weight-bold">P:</span> {{ client.producer }}
        </v-col>
        <v-col cols="6" class="py-0 pt-2 text-left">
          <span v-if="client.producer && client.csr !=- ''" class=".subtitle-1 font-weight-bold">C:</span> {{ client.csr }}
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="py-0 pt-2 text-left caption word-wrap">
          <span class=".subtitle-1 font-weight-bold">Matched On: </span> {{ matchedOn }}
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</v-hover>
</template>

<script>
import analyticsService from '@/services/shared/analyticsService.js';
import { mapState } from 'vuex';

export default {
  name: 'SearchResult',
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      hover: false,
      crossSellCustType: { text: 'Allow Cross Selling', value: 16 },
      commercialCustType: { text: 'Commercial', value: 32 },
      lifeHealthCustType: { text: 'Life/Health', value: 64 },
      personalCustType: { text: 'Personal', value: 128 },
      customerTypes: [],
      // Email, Phone, Name
      conditionalDisplayNameTypes: [2, 4, 5]
    };
  },
  computed: {
    ...mapState('search', ['searchType']),
    isRestricted () {
      return this.client.restricted > 0;
    },
    isAllowCrossSelling () {
      return this.client.permissions & this.crossSellCustType.value;
    },
    isCommercial () {
      return this.client.permissions & this.commercialCustType.value;
    },
    isLifeHealth () {
      return this.client.permissions & this.lifeHealthCustType.value;
    },
    isPersonal () {
      return this.client.permissions & this.personalCustType.value;
    },
    isBusiness () {
      return ((this.client.businessName || null) !== null) && this.client.businessName.length > 0;
    },
    custTypesDisplayText () {
      var displayText = '';
      for (var i = 0; i < this.customerTypes.length; i++) {
        displayText += this.customerTypes[i].text;
        if (this.customerTypes.length > 1 && i !== this.customerTypes.length - 1) {
          displayText += ' | ';
        }
      }
      return displayText;
    },
    mainContactName () {
      if ((this.client || null) === null && (this.client.mainContact || null) === null) return '';
      return this.client.mainContact.formattedName;
    },
    displayName () {
      if (this.conditionalDisplayNameTypes.includes(this.searchType)) {
        for (const match of this.client.summaryMatch) {
          // if the match was on a personal profile and if the personal profile is a named insured or main contact (0 === none), display that info
          if (match.summaryType === 2 && match.personalProfile.namedInsuredOrMainContactType !== 0) {
            return match.personalProfile.formattedName;
          }
        }
      }

      return this.mainContactName;
    },
    matchedOn () {
      if (this.client.searchMatch.length === 0) {
        return 'Unknown';
      }

      // Going off the first search match
      var firstMatch = this.client.searchMatch[0];

      // summary ID
      var matchType = firstMatch.parentType;

      // Find the associated summary match
      var summaryMatch = this.client.summaryMatch.find(sm => sm.summaryId === firstMatch.parentId);

      // What are we searching on?
      switch (this.searchType) {
        // Name
        case 5:
          switch (matchType) {
            // Client
            case 1:
              return this.mainContactName;
            // PersonalProfile
            case 2:
              return `${this.fullNameString(summaryMatch.personalProfile)} - ${this.namedInsuredOrMainContactTypeString(summaryMatch.personalProfile.namedInsuredOrMainContactType)}`;
            // Driver
            case 3:
              return `${this.fullNameString(summaryMatch.personalProfileDriver)} - Driver`;
            case 5:
              return `${this.fullNameString(summaryMatch.personalProfile)} - ${this.namedInsuredOrMainContactTypeString(summaryMatch.personalProfile.namedInsuredOrMainContactType)}`;
            case 6:
              var display = `${this.fullNameString(summaryMatch.personalProfile)} - ${this.namedInsuredOrMainContactTypeString(summaryMatch.personalProfile.namedInsuredOrMainContactType)}`;
              if ((summaryMatch.personalProfile.relationship || null) !== null &&
                   summaryMatch.personalProfile.relationship !== '') {
                display += ` (${summaryMatch.personalProfile.relationship})`;
              }

              return display;
            default:
              return 'Unknown';
          }
        // Business Name
        case 7:
          // if we have multiple search matches, that means we got a hit on both business name and DBA
          if (this.client.searchMatch.length > 1) {
            return this.client.businessName;
          }
          return this.client.searchMatch[0].search;
        // Phone
        case 4:
          switch (matchType) {
            // Client
            case 1:
              return `Client Phone ${firstMatch.search}`;
            // Policy OR Personal
            case 2: case 5: case 6:
              return this.buildPersonalProfileString(summaryMatch, firstMatch);
            default:
              return 'Unknown';
          }
        // Email
        case 2:
          switch (matchType) {
            // Client
            case 1:
              return `Client Email ${firstMatch.search}`;
            // Policy OR Personal
            case 2: case 5: case 6:
              return this.buildPersonalProfileString(summaryMatch, firstMatch);
            default:
              return 'Unknown';
          }
        // Address
        case 3:
          switch (matchType) {
            // Client
            case 1:
              return `${firstMatch.search} - Client`;
            // Policy
            case 4:
              return `${firstMatch.search} - ${summaryMatch.policy.displayOrder}-${summaryMatch.policy.title}`;
            default:
              return 'Unknown';
          }
        default:
          return 'Unknown';
      }
    }
  },
  created () {
    this.buildCustomerTypes();
  },
  methods: {
    doNavigation () {
      if (this.isRestricted) return;

      analyticsService.sendEvent(this.$gtag, 'Launch Client', 'Search', 'Launch Client');
      this.$router.push(`/client/${this.client.clientId}`);
    },
    buildCustomerTypes () {
      if (this.isPersonal) {
        this.customerTypes.push(this.personalCustType);
      }
      if (this.isCommercial) {
        this.customerTypes.push(this.commercialCustType);
      }
      if (this.isLifeHealth) {
        this.customerTypes.push(this.lifeHealthCustType);
      }
      if (this.isAllowCrossSelling) {
        this.customerTypes.push(this.crossSellCustType);
      }
    },
    namedInsuredOrMainContactTypeString (val) {
      if (this.isBusiness) {
        if (val > 0) return 'Main Contact';
      }
      switch (val) {
        case 0:
          return 'Profile';
        case 1:
          return '1st Named Insured';
        case 2:
          return '2nd Named Insured';
        default:
          return 'Named Insured';
      }
    },
    fullNameString (personalProfile) {
      return ((personalProfile || null) === null) ? '' : personalProfile.formattedName;
    },
    buildPersonalProfileString (firstMatch, searchMatch) {
      return `${this.fullNameString(firstMatch.personalProfile)} ${searchMatch.search} - ${this.namedInsuredOrMainContactTypeString(firstMatch.personalProfile.namedInsuredOrMainContactType)}`;
    }
  }
};
</script>

<style lang="scss">
.text-left {
      text-align: left !important;
  }
.word-wrap {
  word-break: normal !important;
}
</style>
